import React, { FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Loader from '@justgiving/loading/es/Loading';
import { useTracking } from '@justgiving/react-tracking';
import classNames from 'classnames';

import { PageStatus } from '../../../types/__generated__';
import { Experiments } from '../../constants/experiments';
import { useApp, useOptimizely, usePageMeta } from '../../hooks';
import useBasePageQuery from '../../hooks/useBasePageQuery';
import { useIsMounted } from '../../hooks/useIsMounted';
import isLightColor from '../../utils/colors/isLightColor';

import styles from './DonateButton.scss';
import { getIsMatchedGiving } from '../../utils/matchedGiving';
import { buildQueryString } from '../../utils/buildQueryString';
import isMobile from '../../utils/isMobile';

type DonateButtonProps = {
  className?: string;
  inactivePageBehaviour?: 'disable' | 'hide';
  onClick?: () => void;
  trackingDataPageSection?: string;
  experiments?: string[];
  successType?: string;
  label?: string;
  id?: string;
};

export const DonateButton: FunctionComponent<DonateButtonProps> = ({
  className,
  inactivePageBehaviour = 'disable',
  onClick = () => {},
  trackingDataPageSection,
  experiments,
  successType,
  label,
  id,
}) => {
  const basePageData = useBasePageQuery();
  const { currentUrl, pageType } = usePageMeta();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation('common', { useSuspense: false });
  const track = useTracking();
  const optimizely = useOptimizely();
  const isMounted = useIsMounted();
  const { showCheckoutPopup } = useApp();

  const urlParams = new URLSearchParams(
    isMounted ? window.location.search : '',
  );
  const queryString = buildQueryString(experiments, successType, urlParams);

  const handleClick = (event: any) => {
    const isMobileDevice = isMobile(window);
    const decision = optimizely?.decide(
      Experiments.ConsumerFintech.CheckoutConversion.LargeBackgroundImage
        .flagKey,
    );

    // Preventing default in all cases, and manually redirecting to checkout, to see if this affects Optimizely bucketing
    event.preventDefault();
    let shouldRedirect = true;

    setLoading(true);
    const donateUrl = `${currentUrl}/donate${buildQueryString(
      experiments,
      successType,
      urlParams,
      decision?.enabled ? 'LargeBackground' : undefined,
    )}`;

    try {
      const popupWidth = 938;
      const popupHeight = 640;

      if (!decision?.enabled && !isMobileDevice) {
        const headlessCheckoutUrl = `${currentUrl}/donate${buildQueryString(
          experiments,
          successType,
          urlParams,
          'Headless',
        )}`;

        setLoading(false);
        shouldRedirect = false;
        showCheckoutPopup(headlessCheckoutUrl, popupWidth, popupHeight);
      }
    } catch (error) {
      // Do nothing
    }

    track({
      page_section: `${pageType} page > ${trackingDataPageSection ||
        'unknown'}`.toLowerCase(),
      event: 'link click',
      action_name: 'link click',
      event_value: 'donate',
      subtype: 'donate',
    });

    if (optimizely) {
      optimizely.track(Experiments.TrackingEvents.donateButtonClick);
    }

    onClick();

    // Preventing default in all cases, and manually redirecting to checkout, to see if this affects Optimizely bucketing
    if (shouldRedirect) window.location.assign(donateUrl);
  };

  type ActiveDonateButtonProps = {
    className?: string;
    href: string;
    id?: string;
  };

  const ActiveDonateButton = ({
    className,
    href,
    id,
  }: ActiveDonateButtonProps) => {
    return (
      <a
        className={className}
        onClick={handleClick}
        href={href}
        data-qa="donateButton"
        id={id}
      >
        {loading ? (
          <Loader size="small" colour="white" />
        ) : (
          <>
            {label ?? t('common.donateButton')}
            {isMatched && <span className={styles.doubleMatchIcon} />}
          </>
        )}
      </a>
    );
  };

  if (!basePageData.data?.page) {
    return (
      <ActiveDonateButton
        href={`${currentUrl}/donate`}
        className={classNames(
          'jg-btn',
          'jg-btn--themed',
          styles.button,
          className,
        )}
        id={id}
      />
    );
  }

  const {
    status,
    relationships,
    branding,
    donationSummary,
  } = basePageData.data.page;

  const isDonateActive = status === PageStatus.Active;

  const isMultiBeneficiaryCampaign =
    relationships && relationships.beneficiaries.totalCount > 1;

  if (inactivePageBehaviour === 'hide' && !isDonateActive) {
    return null;
  }

  const buttonType =
    branding?.primaryColor && isLightColor(branding.primaryColor)
      ? 'jg-btn--ghost'
      : 'jg-btn--themed';

  const isMatched = getIsMatchedGiving(donationSummary);

  const donateLink = isDonateActive ? (
    <ActiveDonateButton
      className={classNames('jg-btn', buttonType, styles.button, className)}
      href={`${currentUrl}/donate${queryString}`}
      id={id}
    />
  ) : (
    <>
      <div
        className={classNames(
          'jg-btn',
          'jg-text--link',
          buttonType,
          styles.button,
          className,
          styles.disabled,
        )}
        data-qa="donateButtonDisabled"
      >
        <span aria-hidden>{label ?? t('common.donateButton')}</span>
        <span className="jg-sr-only">{t('common.donateButtonInactive')}</span>
      </div>
    </>
  );

  return !isMultiBeneficiaryCampaign ? donateLink : null;
};

export default DonateButton;
